import _keyBy from 'lodash/keyBy';

import type { Theme } from '@mui/material/styles';

import { qknColors } from 'app-components/utils/theme';

export const requiredColumns = {
  default: ['amount', 'category', 'split', 'postedOn', 'payee', 'select', 'menu'],
  investment: ['select', 'postedOn', 'action', 'security', 'amount', 'menu'],
};

// TODO: remove with the legacy regFieldsSort preference (deprecatedRegister)
export const fieldData = (isProtectedTxn: boolean) => ({
  account: {
    label: 'Account',
    editable: !isProtectedTxn,
    position: 10,
    moveable: false,
    exportable: true,
    sortable: false,
  },
  select: {
    label: 'Select',
    editable: !isProtectedTxn,
    position: 5,
    moveable: false,
    exportable: false,
    sortable: false,
  },
  check: {
    label: 'Check #',
    editable: !isProtectedTxn,
    position: 28,
    moveable: false,
    exportable: false,
    sortable: true,
    // see account selectors in app components for selective showing on account
  },
  userFlag: {
    label: 'Flag',
    editable: !isProtectedTxn,
    position: 14,
    moveable: false,
    exportable: false,
    sortable: false,
  },
  reviewed: {
    label: 'Reviewed',
    editable: !isProtectedTxn,
    position: 15,
    moveable: true,
    exportable: false,
    sortable: false,
  },
  match: { label: 'Entry Method', editable: !isProtectedTxn, position: 20, moveable: true, exportable: false },
  state: { label: 'Status', editable: !isProtectedTxn, position: 27, moveable: true, exportable: true, sortable: true },
  postedOn: {
    label: 'Date',
    editable: !isProtectedTxn,
    position: 25,
    moveable: true,
    exportable: true,
    sortable: true,
  },
  payee: { label: 'Payee', editable: !isProtectedTxn, position: 35, moveable: true, exportable: true, sortable: true },
  usage: { label: 'Usage', editable: !isProtectedTxn, position: 38, moveable: true, exportable: true, sortable: true },
  category: {
    label: 'Category',
    editable: !isProtectedTxn,
    position: 40,
    moveable: true,
    exportable: true,
    sortable: true,
  },
  split: { label: 'Split', editable: true, position: 40, moveable: false, exportable: false, sortable: false },
  tags: { label: 'Tags', editable: !isProtectedTxn, position: 45, moveable: true, exportable: true, sortable: false },
  clientAndProject: {
    label: 'Client / Project',
    editable: !isProtectedTxn,
    position: 46,
    moveable: false,
    exportable: false,
    sortable: true,
  },
  notes: { label: 'Notes', editable: true, position: 50, moveable: true, exportable: true, sortable: false },
  attachment: {
    label: 'Attachment',
    editable: false,
    position: 60,
    moveable: true,
    exportable: false,
    sortable: false,
  },
  ignored: {
    label: 'Exclusion',
    editable: !isProtectedTxn,
    position: 70,
    moveable: false,
    exportable: false,
    sortable: false,
  },
  recurring: { label: 'Recurring', editable: false, position: 75, moveable: false, exportable: false, sortable: false },
  amount: {
    label: 'Amount',
    editable: !isProtectedTxn,
    position: 85,
    moveable: true,
    exportable: true,
    sortable: true,
  },
  menu: { label: 'Action Menu', editable: true, position: 90, moveable: false, exportable: false, sortable: false },

  // investment transactions
  action: { label: 'Action', editable: true, position: 34, moveable: false, exportable: true, sortable: true },
  security: {
    label: 'Security / Payee',
    editable: true,
    position: 36,
    moveable: false,
    exportable: true,
    sortable: true,
  },
  description: {
    label: 'Description / Category',
    editable: true,
    position: 37,
    moveable: false,
    exportable: true,
    sortable: true,
  },
  quantity: { label: 'Qty', editable: true, position: 72, moveable: false, exportable: true, sortable: true },
  price: { label: 'Price', editable: true, position: 73, moveable: false, exportable: true, sortable: true },
  commission: { label: 'Fee', editable: true, position: 74, moveable: false, exportable: true, sortable: true },
});

// This is the default columns shown, and default sort order
// This is used by QPreferences to initialize the state in preferences

// TODO: remove with the legacy regFieldsSort preference (deprecatedRegister)
export const columnDefaults = {
  default: [
    'select',
    'account',
    'userFlag',
    'postedOn',
    'state',
    'payee',
    'usage',
    'category',
    'split',
    'tags',
    'attachment',
    'ignored',
    'clientAndProject',
    'recurring',
    'amount',
    'menu',
  ],
  manual: [
    'select',
    'account',
    'postedOn',
    'state',
    'payee',
    'usage',
    'category',
    'split',
    'tags',
    'attachment',
    'ignored',
    'clientAndProject',
    'recurring',
    'amount',
    'menu',
  ],
  all: [
    'select',
    'account',
    'userFlag',
    'reviewed',
    'match',
    'postedOn',
    'payee',
    'usage',
    'category',
    'split',
    'tags',
    'notes',
    'attachment',
    'ignored',
    'clientAndProject',
    'recurring',
    'amount',
    'menu',
  ],
  investment: [
    'select',
    'account',
    'postedOn',
    'action',
    'security',
    'description',
    'ignored',
    'quantity',
    'amount',
    'menu',
  ],
};

export enum UserFlagType {
  RED = 'RED',
  ORANGE = 'ORANGE',
  YELLOW = 'YELLOW',
  GREEN = 'GREEN',
  BLUE = 'BLUE',
  VIOLET = 'VIOLET',
  BROWN = 'BROWN',
  BLACK = 'BLACK',
  NONE = 'NONE',
}

export type UserFlag = {
  label: string;
  value: `${UserFlagType}`;
  inMenu: boolean;
};

export type UserFlagWithColor = UserFlag & {
  color: string;
};

export const flagFieldOptions: UserFlag[] = [
  { label: 'Red', value: 'RED', inMenu: true },
  { label: 'Orange', value: 'ORANGE', inMenu: false },
  { label: 'Yellow', value: 'YELLOW', inMenu: true },
  { label: 'Green', value: 'GREEN', inMenu: true },
  { label: 'Blue', value: 'BLUE', inMenu: true },
  { label: 'Violet', value: 'VIOLET', inMenu: true },
  { label: 'Brown', value: 'BROWN', inMenu: false },
  { label: 'Black', value: 'BLACK', inMenu: false },
  { label: 'None', value: 'NONE', inMenu: true },
];

export const flagFieldOptionsById = _keyBy(flagFieldOptions, 'value');

const flagPaletteKeys: Record<`${UserFlagType}`, keyof Theme['palette']['transactionFlags']> = {
  RED: 'red',
  ORANGE: 'orange',
  YELLOW: 'yellow',
  GREEN: 'green',
  BLUE: 'blue',
  VIOLET: 'violet',
  BROWN: 'brown',
  BLACK: 'black',
  NONE: 'none',
};

const getFallbackTransactionFlags = (mode?: Theme['palette']['mode']): Theme['palette']['transactionFlags'] => ({
  red: qknColors.red[600],
  orange: qknColors.deepOrange[600],
  yellow: qknColors.yellow[600],
  green: qknColors.green[600],
  // Figma's flag/blue and flag/violet token names are crossed relative to QCS/user-facing labels.
  blue: qknColors.sky[500],
  violet: qknColors.blue[500],
  brown: qknColors.cream[600],
  black: mode === 'dark' ? qknColors.grey[400] : qknColors.grey[800],
  none: qknColors.transparent[0],
});

const getTransactionFlags = (theme: Theme): Theme['palette']['transactionFlags'] =>
  theme.palette?.transactionFlags ?? getFallbackTransactionFlags(theme.palette?.mode);

export const getFlagSwatchColor = (theme: Theme, value: `${UserFlagType}` | null | undefined): string => {
  const flagValue = value ?? UserFlagType.NONE;
  return getTransactionFlags(theme)[flagPaletteKeys[flagValue]];
};

export const getEmptyFlagIconColor = (theme: Theme): string =>
  theme.palette?.icon?.main ?? theme.palette?.text?.secondary ?? qknColors.grey[700];

export const getFlagIconColor = (theme: Theme, value: `${UserFlagType}` | null | undefined): string => {
  if (!value) {
    return getEmptyFlagIconColor(theme);
  }

  return getFlagSwatchColor(theme, value);
};

export const getFlagFieldOptions = (theme: Theme): UserFlagWithColor[] =>
  flagFieldOptions.map((option) => ({
    ...option,
    color: getFlagSwatchColor(theme, option.value),
  }));

export const clrFieldOptions = {
  // @ts-expect-error FIXME TS7006
  styles: (theme) => ({
    '&.Pending': {
      borderColor: theme.palette.info.main,
      color: theme.palette.info.text,
      background: theme.palette.info.light,
    },
    '&.Cleared': {
      borderColor: theme.palette.inactive.main,
      color: theme.palette.inactive.text,
      background: theme.palette.inactive.light,
    },
    '&.Upcoming': {
      borderColor: theme.palette.active.main,
      color: theme.palette.active.text,
      background: theme.palette.active.light,
    },
    '&.Today': {
      borderColor: theme.palette.active.main,
      color: theme.palette.active.text,
      background: theme.palette.active.light,
    },
    '&.Past': {
      borderColor: theme.palette.warning.main,
      color: theme.palette.warning.text,
      background: theme.palette.warning.light,
    },
    '&.No_Change': {
      borderColor: theme.palette.active.main,
      color: theme.palette.active.text,
      background: theme.palette.active.light,
    },
  }),
  options: [
    { label: 'Pending', state: 'PENDING', menu: true },
    { label: 'Cleared', state: 'CLEARED', menu: true },
    { label: 'Today', state: 'DUE', menu: false },
    { label: 'Past', state: 'OVERDUE', menu: false },
    { label: 'Upcoming', state: 'UPCOMING', menu: false },
  ],
};

//
// This array describes which fields are editable for recurring transactions
//
export const recurringEditable = columnDefaults.default; // ['account', 'menu', 'payee', 'state', 'category','postedOn', 'select', 'amount', 'income', 'expense', 'save', 'close'];

export function getColumnDefaults(type = 'default') {
  const key = type && type.toLowerCase();
  // @ts-expect-error FIXME TS7053
  return columnDefaults[key] || columnDefaults.default;
}
